import React, { Component } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import './SubmittalActions.css';
import printSvg from 'images/print.svg';
import mailSvg from 'images/mail.svg';
import { setShowEmailModal } from 'components/admin/projects/details/project-email-modal/store/actions';
import Button from 'components/shared/button/Button';
import ConfirmationModal from 'components/shared/modal/ConfirmationModal';
import moment from 'moment';
import { TYPES, usersUtil } from 'services/utils/users-util';
import { Enterprise } from 'domain/enterprise';
import { Submittal } from 'domain/submittal';
import {
  SubmittalStatusKeys,
  SubmittalStatusEnum,
} from 'domain/submittal-status-enum';
import {
  setSubmittalFormErrors,
  createSubmittal,
  updateSubmittal,
  updateSubmittalLeadTime,
  respondSubmittal,
  respondSubmittalAddlAssignee,
  setSubmittalReadOnly,
  setSubmittalLeadTimeReadOnly,
  setSubmittalGcAcceleration,
  updateSubmittalFormValue,
  loadTransmittalPdf,
  setSubmittalPdfViewer,
  setSetShowSubmittalErrorMessage,
  deleteSubmittal,
} from 'components/admin/projects/details/project-submittal-log/store/actions';
import {
  getCurrentPage,
  getCurrentStatus,
  getSubmittalForm,
  getSubmittalReadOnly,
  getSubmittalLeadTimeReadOnly,
  getSubmittalGcAcceleration,
  getSubmittalShowErrorMessage,
} from 'components/admin/projects/details/project-submittal-log/store/selectors';

class SubmittalActions extends Component {
  static propTypes = {
    currentUser: PropTypes.shape({}).isRequired,
    project: PropTypes.shape({}).isRequired,
    submittal: PropTypes.shape({}),
    showLeadTime: PropTypes.bool.isRequired,
    handleClose: PropTypes.func.isRequired,
  };

  constructor(props) {
    super(props);

    this.state = { showDeleteConfirmation: false };
  }

  validateForm = (draft = false) => {
    const { project, form, gcAcceleration, dispatch } = this.props;
    const primary = Enterprise.primary(project.contributor_type);
    const errors = {};
    const status = SubmittalStatusEnum.create(form.status);

    if (!primary) {
      if (!form.csiCodeId) {
        errors.csiCodeId = true;
      }
      if (!draft) {
        if (!form.dueDate && !SubmittalStatusEnum.FOR_RECORD.equals(status)) {
          errors.dueDate = true;
        }
        if (!form.status) {
          errors.status = true;
        }
        if (!form.title) {
          errors.title = true;
        }
        if (!form.question) {
          errors.question = true;
        }
      }
    }
    if (!draft && (primary || gcAcceleration) && !form.stampAction) {
      errors.stampAction = true;
    }
    dispatch(setSubmittalFormErrors(errors));
    return Object.keys(errors).length === 0;
  };

  validate = (draft = false) => {
    return this.validateForm(draft);
  };

  formStatus = (draft = false) => {
    const { submittal, form } = this.props;
    const status = submittal && SubmittalStatusEnum.create(submittal.status);
    let newStatus = form.status;
    if (draft) {
      if (!status || SubmittalStatusEnum.DRAFT.equals(status)) {
        newStatus = SubmittalStatusKeys.DRAFT;
      } else if (
        SubmittalStatusEnum.PENDING.equals(status) ||
        SubmittalStatusEnum.DRAFT_PENDING.equals(status)
      ) {
        newStatus = SubmittalStatusKeys.DRAFT_PENDING;
      } else if (
        SubmittalStatusEnum.ASSIGNED.equals(status) ||
        SubmittalStatusEnum.DRAFT_ASSIGNED.equals(status)
      ) {
        newStatus = SubmittalStatusKeys.DRAFT_ASSIGNED;
      }
    } else {
      if (newStatus === 'for_record') {
        newStatus = SubmittalStatusKeys.FOR_RECORD;
      } else {
        newStatus =
          form.stampAction ||
          (form.assignee.length === 0
            ? SubmittalStatusKeys.PENDING
            : SubmittalStatusKeys.ASSIGNED);
      }
    }

    this.handleFormChange('status', newStatus);
    return newStatus;
  };

  handleSaveDraft = () => {
    this.handleSubmit(true);
  };

  handleMarkAsClosed = () => {
    this.handleSubmit(false, true);
  };

  handleSubmit = (draft = false, closed = false) => {
    if (!this.validate(draft)) {
      this.props.dispatch(setSetShowSubmittalErrorMessage(true));
      return;
    }
    const { project, form, gcAcceleration, handleClose, dispatch } = this.props;
    const primary = Enterprise.primary(project.contributor_type);
    const status = SubmittalStatusEnum.create(this.formStatus(draft));
    const submittal = {
      ...form,
      status: status.status,
      assigneeId: form.assignee[0] || '',
      recipientIds: form.recipients.map((recpt) => recpt[0]),
      requestedAt: form.requestedDate ? moment(form.requestedDate) : '',
      closed:
        !!form.closed || closed || !!(gcAcceleration && form.stampAction) || '',
      remarks: form.remarks || '',
      stampAction: form.stampAction || '',
    };
    let promise;
    if (form.id) {
      if (primary) {
        const addlAssignee = Submittal.addlAssignee(
          this.props.submittal,
          this.props.currentUser
        );
        if (addlAssignee) {
          promise = dispatch(
            respondSubmittalAddlAssignee(addlAssignee.id, submittal)
          );
        } else {
          promise = dispatch(respondSubmittal(project.id, form.id, submittal));
        }
      } else {
        promise = dispatch(updateSubmittal(project.id, form.id, submittal));
      }
    } else {
      const { currentPage, currentStatus } = this.props;
      promise = dispatch(
        createSubmittal(project.id, submittal, currentPage, currentStatus)
      );
    }

    promise.then((response) => {
      if (response.ok) {
        if (gcAcceleration) {
          dispatch(respondSubmittal(project.id, form.id, submittal)).then(
            (response) => {
              if (response.ok) {
                handleClose();
              }
            }
          );
        } else {
          handleClose();
        }
      }
    });
  };

  handleFormChange = (attribute, value) => {
    this.props.dispatch(updateSubmittalFormValue(attribute, value));
  };

  setReadOnly = (readOnly) => {
    this.props.dispatch(setSubmittalReadOnly(readOnly));
  };

  handleExportTransmittal = () => {
    const { project, form, dispatch } = this.props;
    dispatch(loadTransmittalPdf(project.id, form.id));
  };

  handleEdit = () => {
    this.setReadOnly(false);
  };

  handleEditLeadTime = () => {
    this.props.dispatch(setSubmittalLeadTimeReadOnly(false));
  };

  handleGcAcceleration = () => {
    this.props.dispatch(setSubmittalGcAcceleration(true));
  };

  handleViewPdf = () => {
    const { dispatch } = this.props;
    dispatch(setSubmittalPdfViewer(true));
  };

  handleShowEmailModal = () => {
    this.props.dispatch(setShowEmailModal(true));
  };

  handleDiscardDraft = () => {
    this.setState({ showDeleteConfirmation: true });
  };

  handleDeleteConfirmation = (confirmed) => {
    this.setState({ showDeleteConfirmation: false });
    confirmed && this.handleDelete();
  };

  handleDelete = () => {
    const { project, submittal, handleClose, dispatch } = this.props;
    dispatch(deleteSubmittal(project.id, submittal.id)).then((response) => {
      if (response.ok) {
        handleClose();
      }
    });
  };

  handleSubmitLeadTime = () => {
    const { project, form, handleClose, dispatch } = this.props;
    const submittal = {
      leadTimeApplies: form.leadTimeApplies,
      leadTimeWeeks: form.leadTimeWeeks,
      orderDate: form.orderDate,
      vendorDelivery: form.vendorDelivery,
      fieldDelivery: form.fieldDelivery,
      leadTimeReminder: form.leadTimeReminder,
    };
    dispatch(updateSubmittalLeadTime(project.id, form.id, submittal)).then(
      (response) => {
        if (response.ok) {
          handleClose();
        }
      }
    );
  };

  render() {
    const {
      currentUser,
      project,
      submittal,
      form,
      readOnly,
      leadTimeReadOnly,
      gcAcceleration,
      showLeadTime,
      showErrorMessage,
      handleClose,
    } = this.props;
    const { showDeleteConfirmation } = this.state;
    const status = SubmittalStatusEnum.create(form.status);
    const discard =
      SubmittalStatusEnum.DRAFT.equals(status) ||
      SubmittalStatusEnum.PENDING.equals(status) ||
      SubmittalStatusEnum.DRAFT_PENDING.equals(status);
    const draftOrPending =
      SubmittalStatusEnum.DRAFT.equals(status) ||
      SubmittalStatusEnum.PENDING.equals(status);
    const pendingOrAssigned =
      SubmittalStatusEnum.PENDING.equals(status) ||
      SubmittalStatusEnum.ASSIGNED.equals(status) ||
      SubmittalStatusEnum.DRAFT_PENDING.equals(status) ||
      SubmittalStatusEnum.DRAFT_ASSIGNED.equals(status);
    const originalStatus =
      submittal && SubmittalStatusEnum.create(submittal.status);
    const primary = Enterprise.primary(project.contributor_type);
    const sub =
      submittal &&
      submittal.contractor_id === currentUser.accountable_id &&
      usersUtil.hasType(currentUser, TYPES.CONTRACTOR);
    const assignedToMe =
      submittal && Submittal.assignee(submittal, currentUser);
    const addlAssignee =
      submittal && Submittal.addlAssignee(submittal, currentUser);
    const spackleOrGc =
      Enterprise.spackle(project.contributor_type) ||
      Enterprise.generalContractor(project.contributor_type);
    const saveDraft =
      !originalStatus ||
      SubmittalStatusEnum.DRAFT.equals(originalStatus) ||
      (spackleOrGc && SubmittalStatusEnum.PENDING.equals(originalStatus)) ||
      (spackleOrGc &&
        SubmittalStatusEnum.DRAFT_PENDING.equals(originalStatus)) ||
      (assignedToMe && SubmittalStatusEnum.ASSIGNED.equals(originalStatus)) ||
      (assignedToMe &&
        SubmittalStatusEnum.DRAFT_ASSIGNED.equals(originalStatus));
    return (
      <div className="submittal-actions">
        {showErrorMessage && (
          <h4 className="error-message">
            There are missing fields, please review.
          </h4>
        )}
        <div className="buttons-container">
          {submittal && (
            <React.Fragment>
              <Button
                className="button-white"
                icon={mailSvg}
                label="Email"
                onClick={this.handleShowEmailModal}
              />
              {submittal.pdf_document && (
                <Button
                  className="button-white"
                  icon={printSvg}
                  label="View PDF"
                  onClick={this.handleViewPdf}
                />
              )}
            </React.Fragment>
          )}
          {!readOnly && saveDraft && !addlAssignee && (
            <Button
              className="button-white button-save-draft"
              label="Save Draft and Close"
              onClick={this.handleSaveDraft}
            />
          )}
          {!readOnly && !gcAcceleration && submittal && spackleOrGc && (
            <Button
              className="button-white"
              label="GC Acceleration"
              onClick={this.handleGcAcceleration}
            />
          )}
          {!readOnly && (
            <Button
              className="button-orange"
              label={form.id ? 'Submit' : 'Create Submittal'}
              onClick={() => this.handleSubmit()}
            />
          )}
          {readOnly &&
            !primary &&
            pendingOrAssigned &&
            form.physicalCopies > '0' && (
              <Button
                className="button-white button-save-draft"
                icon={printSvg}
                label="Export Transmittal"
                onClick={this.handleExportTransmittal}
              />
            )}
          {readOnly &&
            spackleOrGc &&
            !pendingOrAssigned &&
            form.stampAction &&
            !form.closed && (
              <Button
                className="button-white"
                label="Mark as Closed"
                onClick={this.handleMarkAsClosed}
              />
            )}
          {readOnly &&
            leadTimeReadOnly &&
            (spackleOrGc ||
              (assignedToMe && !form.closed) ||
              (sub && draftOrPending)) && (
              <Button
                className="button-orange"
                label="Edit"
                onClick={this.handleEdit}
              />
            )}
          {readOnly &&
            leadTimeReadOnly &&
            showLeadTime &&
            (!sub || !submittal.field_delivery) && (
              <Button
                className="button-orange"
                label="Edit Lead Time"
                onClick={this.handleEditLeadTime}
              />
            )}
          {!leadTimeReadOnly && showLeadTime && (
            <Button
              className="button-orange"
              label="Submit"
              onClick={this.handleSubmitLeadTime}
            />
          )}
          {submittal && discard && (
            <Button
              className="button-white button-delete"
              label="Discard"
              onClick={this.handleDiscardDraft}
            />
          )}
          <Button
            className="button-gray"
            label={readOnly && leadTimeReadOnly ? 'Close' : 'Cancel'}
            onClick={handleClose}
          />
        </div>
        {showDeleteConfirmation && (
          <ConfirmationModal
            show={showDeleteConfirmation}
            message="Are you sure you want to discard this Submittal?"
            onHide={this.handleDeleteConfirmation}
          />
        )}
      </div>
    );
  }
}

export default connect((state) => {
  return {
    currentPage: getCurrentPage(state),
    currentStatus: getCurrentStatus(state),
    form: getSubmittalForm(state),
    readOnly: getSubmittalReadOnly(state),
    leadTimeReadOnly: getSubmittalLeadTimeReadOnly(state),
    gcAcceleration: getSubmittalGcAcceleration(state),
    showErrorMessage: getSubmittalShowErrorMessage(state),
  };
})(SubmittalActions);



// WEBPACK FOOTER //
// ./src/components/admin/projects/details/project-submittal-log/SubmittalActions.js